import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { isNativeApp } from '../utils/nativePayment';
import { getStoreTarget } from '../utils/appLinks';
import { trackEvent } from '../utils/analytics';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const SNOOZE_KEY = 'pbc_app_download_snooze_until'; // absolute ms timestamp; banner hidden until then
const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_SNOOZE_MS = 1 * DAY_MS; // ✕ → re-show on the next visit a day later (at most once/day)
const CTA_SNOOZE_MS = 3 * DAY_MS;     // tapped "Get the app" → re-show after 3 days

// Entrance slide plays once per session, not on every route re-entry (the banner
// is mounted persistently and toggled via the `suppressed` prop, so without this
// it would re-slide every time the customer left and returned to a shown page).
let hasAnimatedIn = false;

/** Apple logo glyph (lucide dropped brand icons) — inherits the button text colour. */
function AppleGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.36 12.78c-.02-2.3 1.88-3.4 1.96-3.45-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.75 2.28-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.29 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.65zM14.13 6.04c.64-.78 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.69-1.1 1.79-.96 2.84 1.02.08 2.06-.52 2.7-1.3z" />
    </svg>
  );
}

/** Google Play triangle glyph (monochrome) — inherits the button text colour. */
function PlayGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.6 2.1c-.3.25-.5.65-.5 1.15v17.5c0 .5.2.9.5 1.15l10.2-9.9zM15.1 13.4l2.7 2.62-11 6.3zM18.9 10.9l2.6 1.5c.8.5.8 1.7 0 2.2l-2.7 1.55-3-2.88zM15.1 10.6 6.8 1.4l11 6.3z" />
    </svg>
  );
}

/**
 * Web-only "Get the app" smart banner. Detects the visitor's phone OS and links
 * to the correct store (iPhone → App Store, Android → Google Play). Renders
 * nothing inside the installed Capacitor app, on desktop, or while snoozed
 * (✕ snoozes 1 day; tapping "Get the app" snoozes 3 days). Otherwise it's
 * present every session. Supersedes the (never-mounted) PWA InstallPrompt now
 * that the app is live on both stores.
 *
 * @param {object} props
 * @param {boolean} [props.suppressed] — route-level hide (nav-less / conversion
 *   pages + Cart, which owns the same bottom strip). Computed by App.js so the
 *   HIDE_NAV_PATHS list stays single-sourced.
 */
export default function AppDownloadBanner({ suppressed = false }) {
  // Welcome Coffee campaign (Business Rule #45). When it's live the subtitle
  // becomes the actual offer — "Get Amazing Offers" is a generic promise, "Your
  // first coffee is on us" is a reason to tap. Flags are cached per page, so
  // this costs nothing beyond the fetch the app already makes.
  const { welcome_coffee: welcomeCoffee } = useFeatureFlags();
  const campaignOn = !!welcomeCoffee?.active;
  const campaignCopy = welcomeCoffee?.copy || {};
  // Platform + dismissal gate — decided once on mount.
  const [eligible, setEligible] = useState(false);
  const [target, setTarget] = useState(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return;       // never inside the installed app
    const t = getStoreTarget();
    if (!t) return;                  // desktop / unknown → no banner (Phase 1)
    try {
      const until = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10);
      if (until && Date.now() < until) return; // still snoozed (✕ = 1 day, "Get the app" = 3 days)
    } catch (e) { /* private mode / disabled storage → show */ }
    setTarget(t);
    setEligible(true);
  }, []);

  const visible = eligible && !suppressed;

  useEffect(() => {
    if (visible && !hasAnimatedIn) {
      hasAnimatedIn = true;
      setAnimate(true);
      // One impression per browsing session (piggybacks the once-per-session
      // animation flag) — powers the AdminAnalytics App Install Nudges card.
      trackEvent('app_banner_shown');
    }
  }, [visible]);

  if (!visible || !target) return null;

  const snooze = (ms) => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms)); } catch (e) { /* ignore */ }
  };
  // ✕ — hide and re-show on the next visit a day later.
  const onDismiss = () => {
    setEligible(false);
    snooze(DISMISS_SNOOZE_MS);
  };
  // Tapped "Get the app" — they've been sent to the store, so back off for 3
  // days before nudging again (the anchor still opens the store in a new tab;
  // hiding the current-tab bar is just immediate feedback).
  const onGetApp = () => {
    trackEvent('app_banner_cta_click', {
      metadata: { platform: target?.platform, welcome_coffee: campaignOn },
    });
    snooze(CTA_SNOOZE_MS);
    setEligible(false);
  };

  return (
    <div
      data-testid="app-download-banner"
      className={`fixed left-0 right-0 z-[48] ${animate ? 'animate-in slide-in-from-bottom-4 duration-300' : ''}`}
      style={{ bottom: 'calc(3.5rem + env(safe-area-inset-bottom))' }}
    >
      <div className="bg-card border-t-2 border-orange-400 shadow-[0_-10px_26px_-18px_rgba(20,16,12,0.35)] flex items-center gap-3 px-3 py-2.5">
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="app-download-dismiss"
          className="flex-shrink-0 w-6 h-6 -ml-0.5 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2.4} />
        </button>

        <img
          src="/logo96.png"
          alt="Penguin Bean Coffee"
          width="44"
          height="44"
          className="w-11 h-11 rounded-xl border border-border object-cover flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-sm leading-tight truncate">
            {campaignOn ? (campaignCopy.banner_title || 'Penguin Bean Coffee') : 'Penguin Bean Coffee'}
          </p>
          <p
            className={`text-[11.5px] leading-tight truncate mt-0.5 ${
              campaignOn ? 'text-[hsl(var(--accent))] font-bold' : 'text-muted-foreground'
            }`}
          >
            {campaignOn
              ? (campaignCopy.banner_sub || 'Your first coffee is on us')
              : 'Get Amazing Offers'}
          </p>
        </div>

        <a
          href={target.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onGetApp}
          data-testid="app-download-cta"
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-accent text-accent-foreground font-bold text-[13px] leading-none rounded-full px-3.5 py-2.5 shadow-[0_6px_16px_-8px_hsla(25,90%,45%,0.7)] active:scale-95 transition-transform"
        >
          {target.platform === 'ios' ? <AppleGlyph /> : <PlayGlyph />}
          Get the app
        </a>
      </div>
    </div>
  );
}
